import { Download, File as FileIcon, Trash2, Upload } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError } from "@/auth";
import { timeAgo } from "@/components/projects/common";

const size = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

export function FilesTab({ project }) {
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(null);
  const input = useRef(null);
  const base = `/projects/${project.id}/files`;
  const load = useCallback(() => api.get(base).then(({ data }) => setItems(data)).catch(() => {}), [base]);
  useEffect(() => { load(); }, [load]);
  const upload = async (files) => {
    for (const f of files) {
      if (f.size > 20 * 1048576) { toast.error(`${f.name} dépasse 20 Mo.`); continue; }
      const fd = new FormData(); fd.append("file", f);
      setProgress({ name: f.name, pct: 0 });
      try { await api.post(base, fd, { timeout: 300000, onUploadProgress: (e) => setProgress({ name: f.name, pct: Math.round((e.loaded / (e.total || 1)) * 100) }) }); } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    }
    setProgress(null); load();
  };
  const download = async (f) => {
    const { data } = await api.get(`${base}/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = f.filename; a.click(); URL.revokeObjectURL(url);
  };
  const remove = async (f) => { if (window.confirm(`Supprimer ${f.filename} ?`)) { await api.delete(`${base}/${f.id}`); load(); } };
  return (
    <div data-testid="files-tab">
      <div className={`pj-drop ${progress ? "is-busy" : ""}`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload([...e.dataTransfer.files]); }} onClick={() => input.current?.click()} data-testid="files-dropzone">
        <Upload size={22} />
        {progress ? <span data-testid="files-progress">Envoi de {progress.name}… {progress.pct}%</span> : <span>Glissez vos fichiers ici ou cliquez pour choisir (20 Mo max.)</span>}
        <input ref={input} type="file" multiple hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid="files-input" />
      </div>
      {items.length === 0 && <p className="pj-empty">Aucun fichier pour l'instant.</p>}
      <ul className="pj-files">
        {items.map((f) => (
          <li key={f.id} data-testid={`file-${f.id}`}>
            <FileIcon size={20} /><div><strong>{f.filename}</strong><small>{size(f.size)} · {f.uploaded_by} · {timeAgo(f.created_at)}</small></div>
            <button type="button" className="pj-icon-btn" onClick={() => download(f)} aria-label="Télécharger" data-testid={`file-download-${f.id}`}><Download size={16} /></button>
            <button type="button" className="pj-icon-btn" onClick={() => remove(f)} aria-label="Supprimer" data-testid={`file-delete-${f.id}`}><Trash2 size={16} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
